import {useEffect, useRef, useState, type RefObject} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {findScrollableAncestor} from '../../hooks/useAvailableSizePx';

type FloatingHorizontalScrollbarProps = {
  bottomSentinelRef: RefObject<HTMLDivElement | null>;
  isColumnsReady: boolean;
  scrollAreaRef: RefObject<HTMLDivElement | null>;
};

type FloatingScrollbarLayout = {
  bottom: number;
  left: number;
  width: number;
  contentWidth: number;
  visible: boolean;
};

const HIDDEN_LAYOUT: FloatingScrollbarLayout = {
  bottom: 0,
  left: 0,
  width: 0,
  contentWidth: 0,
  visible: false,
};

const SCROLLBAR_CLASSES =
  '[&::-webkit-scrollbar]:h-2.5' +
  ' [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300' +
  ' [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid' +
  ' [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-padding' +
  ' [&::-webkit-scrollbar-thumb:hover]:bg-slate-400' +
  ' [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent';

function getLayout(scrollArea: HTMLDivElement): FloatingScrollbarLayout {
  const scrollAncestor = findScrollableAncestor(scrollArea);
  const scrollAreaRect = scrollArea.getBoundingClientRect();
  const ancestorRect = scrollAncestor?.getBoundingClientRect();
  const viewportBottom = Math.min(window.innerHeight, ancestorRect?.bottom ?? window.innerHeight);
  const viewportLeft = Math.max(0, ancestorRect?.left ?? 0);
  const viewportRight = Math.min(window.innerWidth, ancestorRect?.right ?? window.innerWidth);
  const left = Math.max(scrollAreaRect.left, viewportLeft);
  const right = Math.min(scrollAreaRect.right, viewportRight);
  const width = Math.max(0, right - left);
  const visible =
    scrollArea.scrollWidth > scrollArea.clientWidth &&
    width > 0 &&
    scrollAreaRect.top < viewportBottom &&
    scrollAreaRect.bottom > viewportBottom;

  return {
    bottom: Math.max(0, window.innerHeight - viewportBottom),
    left,
    width,
    contentWidth: scrollArea.scrollWidth,
    visible,
  };
}

/**
 * Mirrors an auto-height table's horizontal scroll position at the bottom of
 * the visible scroll viewport while the table's native scrollbar is below it.
 */
export function FloatingHorizontalScrollbar(props: FloatingHorizontalScrollbarProps) {
  const floatingRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<FloatingScrollbarLayout>(HIDDEN_LAYOUT);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: external scroll/resize subscriptions require cleanup
  useEffect(
    function syncFloatingScrollbar() {
      const scrollArea = props.scrollAreaRef.current;
      const floatingScrollbar = floatingRef.current;

      if (!scrollArea || !floatingScrollbar) {
        return;
      }

      let scrollAncestor: HTMLElement | null = null;
      const updateLayout = () => {
        const nextScrollAncestor = findScrollableAncestor(scrollArea);
        if (nextScrollAncestor !== scrollAncestor) {
          scrollAncestor?.removeEventListener('scroll', updateLayout);
          nextScrollAncestor?.addEventListener('scroll', updateLayout);
          scrollAncestor = nextScrollAncestor;
        }
        setLayout(getLayout(scrollArea));
      };
      const syncFromTable = () => {
        floatingScrollbar.scrollLeft = scrollArea.scrollLeft;
      };
      const syncFromFloatingScrollbar = () => {
        scrollArea.scrollLeft = floatingScrollbar.scrollLeft;
      };

      updateLayout();
      syncFromTable();

      const resizeObserver = new ResizeObserver(updateLayout);
      resizeObserver.observe(scrollArea);
      if (isNotNil(scrollArea.firstElementChild)) {
        resizeObserver.observe(scrollArea.firstElementChild);
      }

      const intersectionObserver =
        typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(updateLayout);
      intersectionObserver?.observe(scrollArea);
      if (isNotNil(props.bottomSentinelRef.current)) {
        intersectionObserver?.observe(props.bottomSentinelRef.current);
      }

      scrollArea.addEventListener('scroll', syncFromTable);
      floatingScrollbar.addEventListener('scroll', syncFromFloatingScrollbar);
      window.addEventListener('scroll', updateLayout);
      window.addEventListener('resize', updateLayout);

      return () => {
        resizeObserver.disconnect();
        intersectionObserver?.disconnect();
        scrollArea.removeEventListener('scroll', syncFromTable);
        floatingScrollbar.removeEventListener('scroll', syncFromFloatingScrollbar);
        scrollAncestor?.removeEventListener('scroll', updateLayout);
        window.removeEventListener('scroll', updateLayout);
        window.removeEventListener('resize', updateLayout);
      };
    },
    [props.bottomSentinelRef, props.isColumnsReady, props.scrollAreaRef]
  );

  return (
    <div
      ref={floatingRef}
      data-testid="eagGridFloatingHorizontalScrollbar"
      aria-hidden
      className={`fixed h-2.5 overflow-x-auto overflow-y-hidden bg-white z-[var(--z-sidebar)] ${SCROLLBAR_CLASSES} ${layout.visible ? '' : 'hidden'}`}
      style={{bottom: layout.bottom, left: layout.left, width: layout.width}}
    >
      <div style={{width: layout.contentWidth, height: 1}} />
    </div>
  );
}
